import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Inbox, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatCurrency } from '@/components/banker-recon/types';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { toNumber, type CompletedServicingPayment } from '@/types/servicing';

type SortField =
  | 'receivedDate'
  | 'dueDate'
  | 'loanNumber'
  | 'borrower'
  | 'principal'
  | 'interest'
  | 'escrow'
  | 'total';

type SortDirection = 'asc' | 'desc';

interface CompletedPaymentsTableProps {
  rows: CompletedServicingPayment[];
  loading: boolean;
  onSelectLoan: (row: CompletedServicingPayment) => void;
}

function sortValue(row: CompletedServicingPayment, field: SortField): string | number {
  switch (field) {
    case 'receivedDate':
      return row.payment.payment_received_date || '';
    case 'dueDate':
      return row.payment.due_date;
    case 'loanNumber':
      return row.loan.loan_number.toLowerCase();
    case 'borrower':
      return (row.loan.borrower_name || '').toLowerCase();
    case 'principal':
      return toNumber(row.payment.principal_received);
    case 'interest':
      return toNumber(row.payment.interest_received) + toNumber(row.payment.late_fee);
    case 'escrow':
      return toNumber(row.payment.escrow_received);
    case 'total':
      return toNumber(row.payment.total_amount_received);
    default:
      return '';
  }
}

export function CompletedPaymentsTable({ rows, loading, onSelectLoan }: CompletedPaymentsTableProps) {
  const [sortField, setSortField] = useState<SortField>('receivedDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedRows = useMemo(() => [...rows].sort((a, b) => {
    const aValue = sortValue(a, sortField);
    const bValue = sortValue(b, sortField);
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === 'asc' ? comparison : -comparison;
  }), [rows, sortDirection, sortField]);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection(field === 'receivedDate' ? 'desc' : 'asc');
  };

  const header = (field: SortField, label: string, alignRight = false) => {
    const active = field === sortField;
    return (
      <TableHead
        className={alignRight ? 'text-right' : undefined}
        aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={alignRight ? '-mr-3 ml-auto h-8 px-2' : '-ml-3 h-8 px-2'}
          onClick={() => toggleSort(field)}
          aria-label={`Sort ${label} ${active && sortDirection === 'asc' ? 'descending' : 'ascending'}`}
        >
          {label}
          {active
            ? sortDirection === 'asc'
              ? <ArrowUp className="ml-1 h-3.5 w-3.5" />
              : <ArrowDown className="ml-1 h-3.5 w-3.5" />
            : <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground" />}
        </Button>
      </TableHead>
    );
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading completed payments...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Inbox className="mb-3 h-10 w-10 opacity-40" />
        <p className="text-sm font-medium">No completed payments match this period</p>
        <p className="mt-1 text-xs">Adjust the received-date range or search filters.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="min-w-[1240px]">
        <TableHeader>
          <TableRow>
            {header('receivedDate', 'Received Date')}
            {header('dueDate', 'Due Date')}
            {header('loanNumber', 'Loan #')}
            {header('borrower', 'Borrower')}
            <TableHead>LO / LO2</TableHead>
            <TableHead>Investor</TableHead>
            <TableHead className="text-center">Payment #</TableHead>
            {header('principal', 'Principal', true)}
            {header('interest', 'Interest', true)}
            {header('escrow', 'Escrow', true)}
            {header('total', 'Total', true)}
            <TableHead>Method / Reference</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow
              key={row.payment.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSelectLoan(row)}
            >
              <TableCell>{formatDateDisplay(row.payment.payment_received_date)}</TableCell>
              <TableCell>{formatDateDisplay(row.payment.due_date)}</TableCell>
              <TableCell className="font-mono font-medium">{row.loan.loan_number}</TableCell>
              <TableCell>{row.loan.borrower_name || '--'}</TableCell>
              <TableCell>{row.loan.loan_officer_display || '--'}</TableCell>
              <TableCell>{row.loan.investor_name || '--'}</TableCell>
              <TableCell className="text-center">{row.payment.payment_number}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(toNumber(row.payment.principal_received))}</TableCell>
              <TableCell className="text-right font-mono text-violet-600">{formatCurrency(toNumber(row.payment.interest_received) + toNumber(row.payment.late_fee))}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(toNumber(row.payment.escrow_received))}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{formatCurrency(toNumber(row.payment.total_amount_received))}</TableCell>
              <TableCell>
                {[row.payment.payment_method, row.payment.reference_number].filter(Boolean).join(' / ') || '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={7} className="font-bold">TOTAL ({rows.length} payments)</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(rows.reduce((sum, row) => sum + toNumber(row.payment.principal_received), 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold text-violet-600">{formatCurrency(rows.reduce((sum, row) => sum + toNumber(row.payment.interest_received) + toNumber(row.payment.late_fee), 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(rows.reduce((sum, row) => sum + toNumber(row.payment.escrow_received), 0))}</TableCell>
            <TableCell className="text-right font-mono font-bold">{formatCurrency(rows.reduce((sum, row) => sum + toNumber(row.payment.total_amount_received), 0))}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

